import React from 'react'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_DECK_STATE } from 'redux/deck/actions/types'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'
import PlaygroupGGService from 'services/playgroupGG.service'

import PhatInput from 'components/formElements/PhatInput'
import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'

import styles from './playgroupForm.module.scss'

const PlaygroupForm = () => {
  const deckId = useAppSelector(state => state.deck.id)
  const playgroupDeckUrl = useAppSelector(state => state.deck.playgroupDeckUrl)

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const [deckUrl, setDeckUrl] = React.useState(playgroupDeckUrl || '')
  const [loading, setLoading] = React.useState(false)

  const handleUpdateDeck = async () => {
    setLoading(true)

    if (!deckUrl)
      return DeckService.update(deckId, { playgroupDeckUrl: '' })
        .then(() => ToastService.create('Playgroup.gg URL removed', 'Deck service', 'success'))
        .catch(() => ToastService.create('Failed to update Playgroup.gg deck URL', 'Deck service', 'error'))
        .finally(() => setLoading(false))

    try {
      await PlaygroupGGService.getDeckFromUrl(deckUrl)
    } catch {
      setLoading(false)

      return ToastService.create(
        'Invalid Playgroup.gg deck URL or the deck is private on Playgroup.gg',
        'Deck service',
        'error',
      )
    }

    return DeckService.update(deckId, { playgroupDeckUrl: deckUrl })
      .then(() => {
        ToastService.create('Playgroup.gg deck URL updated', 'Deck service', 'success')

        setDeckState({ playgroupDeckUrl: deckUrl })
      })
      .catch(() => ToastService.create('Failed to update Playgroup.gg deck URL', 'Deck service', 'error'))
      .finally(() => setLoading(false))
  }

  const PlaygroupLink = (
    <Link href="https://playgroup.gg" target="_blank" className={styles.link}>
      Playgroup.gg
    </Link>
  )

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.logo}>
          <div className={styles.logoName}>
            <span>Playgroup.gg</span>
            <span>EDH Tracker</span>
          </div>
          <img src="/images/playgroup_gg.svg" title="Playgroup.gg" alt="playgroup dot gg" />
        </div>
      </div>

      <p>
        {PlaygroupLink} is third party EDH deck stat tracking software. Use it to keep track of your EDH game wins,
        losses, and more. Check out all of the features on {PlaygroupLink}. Linking your deck will allow you to see the
        power level for this deck in the deck info at the top of the deck page as well as offer a convenient link to
        your deck on your Playgroup.gg account.
      </p>

      <p>
        The deck on Playgroup.gg must be public for this to work. If you don't have a Playgroup.gg account, you can
        create one{' '}
        <Link className={styles.link} href="https://playgroup.gg/users/sign_up" target="_blank">
          here
        </Link>
        .
      </p>

      <PhatInput
        header="Playgroup.gg deck URL"
        placeholder="https://playgroup.gg/decks/75110-squirrels-and-tokens"
        containerClassName={styles.input}
        value={deckUrl}
        onChange={setDeckUrl}
        disabled={loading}
      />

      <div className={styles.controls} onClick={handleUpdateDeck}>
        <PhatButton color="green" disabled={false} icon="save outline">
          Save Playgroup.gg deck URL
        </PhatButton>
      </div>
    </div>
  )
}

export default PlaygroupForm
